/* client/src/components/Admin/BusinessUnits.tsx */
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Chip,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiGetBusinessUnits, apiAddBusinessUnit, apiUpdateBusinessUnit, apiDeleteBusinessUnit, apiGetBusinessUnitTypes, apiGetPartners } from '../../services/apiService';

interface BusinessUnit {
  id: number;
  name: string;
  description: string;
  type_id: number;
  type_name: string;
  created_at: string;
}

interface BusinessUnitType {
  id: number;
  name: string;
  description: string;
}

interface Partner {
  id: number;
  name: string;
  email: string;
}

function BusinessUnits() {
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [businessUnitTypes, setBusinessUnitTypes] = useState<BusinessUnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<BusinessUnit | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type_id: '',
  });

  // Add partnership share state variables
  const [partnershipShares, setPartnershipShares] = useState<Array<{partner_id: string, percentage: string}>>([]);
  const [availablePartners, setAvailablePartners] = useState<Partner[]>([]);

  useEffect(() => {
    fetchBusinessUnits();
    fetchBusinessUnitTypes();
    fetchPartners();
  }, []);

  const fetchBusinessUnits = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiGetBusinessUnits();
      setBusinessUnits(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch business units');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUnitTypes = async () => {
    try {
      const data = await apiGetBusinessUnitTypes();
      setBusinessUnitTypes(data);
    } catch (err: any) {
      console.error('Failed to fetch business unit types:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const data = await apiGetPartners();
      setAvailablePartners(data);
    } catch (err: any) {
      console.error('Failed to fetch partners:', err);
    }
  };

  const handleOpen = (unit: BusinessUnit | null = null) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        name: unit.name,
        description: unit.description || '',
        type_id: unit.type_id?.toString() || '',
      });
      // For editing, you might want to load existing partnership shares
      // This would require an API endpoint to get shares for a specific business unit
      setPartnershipShares([]);
    } else {
      setEditingUnit(null);
      setFormData({
        name: '',
        description: '',
        type_id: '',
      });
      setPartnershipShares([]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUnit(null);
    setPartnershipShares([]);
  };

  const handlePartnershipShareChange = (index: number, field: string, value: string) => {
    const updatedShares = [...partnershipShares];
    updatedShares[index] = { ...updatedShares[index], [field]: value };
    setPartnershipShares(updatedShares);
  };

  const addPartnershipShare = () => {
    setPartnershipShares([...partnershipShares, { partner_id: '', percentage: '' }]);
  };

  const removePartnershipShare = (index: number) => {
    const updatedShares = [...partnershipShares];
    updatedShares.splice(index, 1);
    setPartnershipShares(updatedShares);
  };

  // Add validation to prevent duplicate partner selection
  const isPartnerAlreadySelected = (partnerId: string, currentIndex: number) => {
    return partnershipShares.some((share, index) => 
      index !== currentIndex && share.partner_id === partnerId
    );
  };

  // Add automatic percentage calculation helper
  const autoDistributePercentage = () => {
    if (partnershipShares.length > 0) {
      const filledShares = partnershipShares.filter(share => share.partner_id && share.percentage);
      if (filledShares.length === partnershipShares.length - 1) {
        const totalSoFar = filledShares.reduce((sum, share) => sum + parseFloat(share.percentage), 0);
        const remaining = 100 - totalSoFar;
        
        const updatedShares = [...partnershipShares];
        const emptyIndex = updatedShares.findIndex(share => !share.partner_id || !share.percentage);
        
        if (emptyIndex !== -1 && remaining >= 0) {
          updatedShares[emptyIndex] = {
            ...updatedShares[emptyIndex],
            percentage: remaining.toFixed(2)
          };
          setPartnershipShares(updatedShares);
        }
      }
    }
  };

  // Call this function when percentages change
  const handlePercentageChange = (index: number, value: string) => {
    handlePartnershipShareChange(index, 'percentage', value);
    setTimeout(autoDistributePercentage, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Validate partnership shares
      if (partnershipShares.length === 0) {
        setError('At least one partnership share is required');
        return;
      }

      const totalPercentage = partnershipShares.reduce((sum, share) => {
        return sum + (share.percentage ? parseFloat(share.percentage) : 0);
      }, 0);

      if (Math.abs(totalPercentage - 100) > 0.01) {
        setError('Partnership shares must total exactly 100%');
        return;
      }

      const unitData = {
        ...formData,
        type_id: formData.type_id ? parseInt(formData.type_id) : null,
        partnership_shares: partnershipShares.map(share => ({
          partner_id: parseInt(share.partner_id),
          percentage: parseFloat(share.percentage)
        }))
      };

      if (editingUnit) {
        await apiUpdateBusinessUnit(editingUnit.id, unitData);
      } else {
        await apiAddBusinessUnit(unitData);
      }
      fetchBusinessUnits();
      handleClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingUnit ? 'update' : 'add'} business unit`);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this business unit?')) {
      try {
        await apiDeleteBusinessUnit(id);
        fetchBusinessUnits();
      } catch (err: any) {
        setError(err.message || 'Failed to delete business unit');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Business Units Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Business Unit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {businessUnits.map((unit) => (
              <TableRow key={unit.id}>
                <TableCell>{unit.name}</TableCell>
                <TableCell>{unit.type_name || 'No Type'}</TableCell>
                <TableCell>{unit.description}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(unit)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(unit.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {businessUnits.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No business units found</Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUnit ? 'Edit Business Unit' : 'Add New Business Unit'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            <TextField
              margin="dense"
              name="type_id"
              label="Business Unit Type"
              select
              fullWidth
              variant="outlined"
              value={formData.type_id}
              onChange={(e) => setFormData({...formData, type_id: e.target.value})}
            >
              <MenuItem value="">No Type</MenuItem>
              {businessUnitTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              multiline
              rows={3}
            />

            {/* Partnership Share Form - Now Mandatory */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Partnership Shares (Required - Must total 100%)
            </Typography>
            
            {partnershipShares.map((share, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  select
                  label="Partner"
                  value={share.partner_id}
                  onChange={(e) => handlePartnershipShareChange(index, 'partner_id', e.target.value)}
                  sx={{ flex: 2 }}
                  size="small"
                  error={isPartnerAlreadySelected(share.partner_id, index)}
                  helperText={isPartnerAlreadySelected(share.partner_id, index) ? 'Partner already selected' : ''}
                  required
                >
                  <MenuItem value="">Select Partner</MenuItem>
                  {availablePartners.map((partner) => (
                    <MenuItem 
                      key={partner.id} 
                      value={partner.id}
                      disabled={isPartnerAlreadySelected(partner.id.toString(), index)}
                    >
                      {partner.name}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  label="Percentage %"
                  type="number"
                  value={share.percentage}
                  onChange={(e) => handlePercentageChange(index, e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ flex: 1 }}
                  size="small"
                  required
                />
                
                <IconButton 
                  onClick={() => removePartnershipShare(index)} 
                  size="small"
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            
            <Button 
              variant="outlined" 
              onClick={addPartnershipShare}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              size="small"
            >
              Add Partner Share
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingUnit ? 'Update' : 'Add'} Business Unit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default BusinessUnits;